import axios from "axios";
import keycloakStore from "@/businesslogic/stores/keycloakStore";
import { Anfrage } from "@/model/Anfrage";
import tracking from "@/businesslogic/tracking/tracking";
import { ZusaetzlicheDaten } from "@/businesslogic/tracking/zusaetzlicheDaten";
import router from "@/router";
import { Vorversicherung } from "@/model/Vorversicherung";
import Logger from "js-logger";
import { Aussteuerungsgrund } from "@/businesslogic/tracking/trackingData";
import anfrageBereinigung from "@/businesslogic/utils/anfrageBereinigung";
import storeUtils from "@/businesslogic/utils/storeUtils";

class AnfrageService {
  http() {
    const keycloakAccessToken = keycloakStore().getKeycloakAccessToken();

    let headers;
    if (keycloakAccessToken) {
      headers = {
        Authorization: "Bearer " + keycloakAccessToken,
        "Content-Type": "application/json",
      };
    } else {
      headers = {
        "Content-Type": "application/json",
      };
    }

    return axios.create({
      baseURL: process.env.VUE_APP_BACKEND_URL,
      headers: headers,
    });
  }

  getBaseUrl(isMakler: boolean, isMaklerLink: boolean) {
    if (!isMakler) {
      return "/api/direkt/";
    }
    return isMaklerLink ? "/api/makler-tarifrechner/" : "/api/makler/";
  }

  private static postProcess(anfrage: Anfrage): Anfrage {
    const anfrageKopie: Anfrage = JSON.parse(JSON.stringify(anfrage));
    anfrageKopie.zahlungsdaten.iban = anfrageKopie.zahlungsdaten.iban.replace(/ /g, "");
    // Bereinigt Anfrage vor dem Absenden ans Backend (u.a. TARGET-4788)
    anfrageBereinigung(anfrageKopie);
    return anfrageKopie;
  }

  private static trackFerienhaus(sessionToken: string, data: any, kontext: string) {
    const statusListe = data.anfrage.buendelStatusListe;
    statusListe.forEach(function (value: any) {
      const vorversicherung = AnfrageService.ermittleFerienhausVorversicherung(value, data);
      const antragId = AnfrageService.ermittleFerienhausAntragId(value, data);
      const aussteuerungsGruende = AnfrageService.ermittleAussteuerungsGruende(value, data);
      AnfrageService.track(
        sessionToken,
        kontext,
        AnfrageService.ermittleBeantragenEreignis(value.status, vorversicherung, aussteuerungsGruende),
        data.anfrage,
        {
          antragId: antragId,
          aussteuerungsgruende: aussteuerungsGruende,
        },
      );
    });
  }

  private static ermittleAussteuerungsGruende(value: any, data: any) {
    let aussteuerung: Aussteuerungsgrund[] = [];
    if ("ABGELEHNT" !== value.status) {
      data.anfrage.buendelAussteuerungListe.forEach(function (aussteuerungElement: any) {
        if (value.productId === aussteuerungElement.productId) {
          aussteuerung = aussteuerungElement.aussteuerung;
        }
      });
    }

    return aussteuerung;
  }

  private static track(
    sessionToken: string,
    kontext: string,
    ereignis: string,
    anfrage?: Anfrage,
    zusaetzlicheDaten?: ZusaetzlicheDaten,
  ) {
    const { track } = tracking();
    track(sessionToken, kontext, ereignis, anfrage, zusaetzlicheDaten);
  }

  berechnen(
    sessionToken: string,
    anfrage: Anfrage,
    kontext: string,
    zeigeSpinner: any,
    okFunction: any,
    retryFunction: any,
    retryCount = 0,
  ) {
    zeigeSpinner.value = true;
    Logger.debug("berechnen(): Sende Daten...", anfrage, retryCount);
    AnfrageService.track(sessionToken, kontext, "neu_berechnen", anfrage);

    this.http()
      .post(
        this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) + "vorschlag",
        AnfrageService.postProcess(anfrage),
        {
          timeout: process.env.VUE_APP_BACKEND_TIMEOUT,
          headers: {
            S_Token: sessionToken,
          },
        },
      )
      .then((value) => {
        okFunction(value);
        zeigeSpinner.value = false;
      })
      .catch((err) => {
        zeigeSpinner.value = false;
        if ("ECONNABORTED" !== err.code) {
          Logger.debug("Anzeige der Fehlerseite", err);
          router.push("/fehler");
        }
        if (retryCount < 3) {
          retryFunction(() =>
            this.berechnen(sessionToken, anfrage, kontext, zeigeSpinner, okFunction, retryFunction, retryCount + 1),
          );
        } else {
          if (err.response.status == 400) {
            return;
          }
          Logger.debug("Anzeige der Fehlerseite", err);
          router.push("/fehler");
        }
      });
    Logger.debug("ergebnis: ", anfrage);
  }

  speichern(sessionToken: string, anfrage: Anfrage, isAntrag: boolean, kontext: string) {
    Logger.debug("speichern(): Sende Daten...", anfrage);
    return this.http()
      .post(
        this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) + "vorschlag/" + (isAntrag ? "a" : "v") + "/speichern",
        AnfrageService.postProcess(anfrage),
        {
          headers: {
            S_Token: sessionToken,
          },
        },
      )
      .then((value) => {
        anfrage.anfrageNummer = value.data.anfrageNummer;
        anfrage.erstelldatum = value.data.erstelldatum;

        AnfrageService.track(sessionToken, kontext, "zwischenspeichern", anfrage);
        return value;
      });
  }

  versenden(sessionToken: string, anfrage: Anfrage, isAntrag: boolean, kontext: string) {
    Logger.debug("versenden(): Sende Daten...", anfrage);
    return this.http()
      .post(
        this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) + "vorschlag/" + (isAntrag ? "a" : "v") + "/versenden",
        AnfrageService.postProcess(anfrage),
        {
          headers: {
            S_Token: sessionToken,
          },
        },
      )
      .then((value) => {
        anfrage.anfrageNummer = value.data.anfrageNummer;
        anfrage.erstelldatum = value.data.erstelldatum;

        AnfrageService.track(sessionToken, kontext, "email_versenden", anfrage);
        return value;
      });
  }

  herunterladen(
    sessionToken: string,
    anfrage: Anfrage,
    isAntrag: boolean,
    kontext: string,
    trackingZusatz: string | undefined,
  ) {
    Logger.debug("herunterladen(): Dokument herunterladen...", anfrage);
    if (trackingZusatz) {
      trackingZusatz = "_" + trackingZusatz;
    }

    return this.http()
      .post(
        this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) +
          "vorschlag/" +
          (isAntrag ? "a" : "v") +
          "/herunterladen?vorgangAnlegen=" +
          (kontext === "Abschluss" ? "false" : "true"),
        AnfrageService.postProcess(anfrage),
        {
          headers: {
            S_Token: sessionToken,
          },
        },
      )
      .then((value) => {
        anfrage.anfrageNummer = value.data.anfrageNummer;
        anfrage.erstelldatum = value.data.erstelldatum;

        AnfrageService.track(sessionToken, kontext, "herunterladen" + trackingZusatz, anfrage);
        return value;
      });
  }

  pruefen(sessionToken: string, anfrage: Anfrage, kontext: string) {
    Logger.debug("pruefen(): Anfrage prüfen...", anfrage);
    const data = AnfrageService.postProcess(anfrage);
    return this.http()
      .post(this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) + "antrag/pruefen", data, {
        headers: {
          S_Token: sessionToken,
        },
      })
      .then((value) => {
        if (true === value.data.aussteuerung) {
          AnfrageService.track(sessionToken, kontext, "aussteuerung", anfrage);
        }
        return value;
      });
  }

  async absenden(sessionToken: string, anfrage: Anfrage, kontext: string) {
    Logger.debug("absenden(): Anfrage absenden...", anfrage);
    AnfrageService.track(sessionToken, kontext, "beantragen", anfrage);
    const data = AnfrageService.postProcess(anfrage);

    Logger.debug("Data to send", data);

    return this.http()
      .post(this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) + "antrag", data, {
        headers: {
          S_Token: sessionToken,
        },
      })
      .then((value) => {
        Logger.debug("value ", value);

        const { istFhkVerkaufsprodukt } = storeUtils();

        if (!istFhkVerkaufsprodukt(value.data.anfrage)) {
          AnfrageService.track(
            sessionToken,
            kontext,
            AnfrageService.ermittleBeantragenEreignis(
              value.data.anfrage.status,
              value.data.anfrage.vorversicherung,
              value.data.kubusData.aussteuerungsReasons,
            ),
            anfrage,
            {
              antragId: value.data.kubusData.antragId,
              aussteuerungsgruende: value.data.kubusData.aussteuerungsReasons,
            },
          );
        } else {
          AnfrageService.trackFerienhaus(sessionToken, value.data, kontext);
        }
        return value;
      })
      .catch(function (error) {
        const fehler = "Antrag konnte nicht übermittelt werden (" + error?.message + ").";
        Logger.error(fehler + "\n" + error?.toJSON());
        AnfrageService.track(sessionToken, kontext, "beantragenFehler_" + error?.message.code, anfrage);
        throw new Error(fehler);
      });
  }

  private static ermittleFerienhausAntragId(value: any, data: any) {
    let antragId = data.anfrage.anfrageNummer;
    if ("ABGELEHNT" !== value.status) {
      data.anfrage.buendelAnfrageNummerListe.forEach(function (idElement: any) {
        if (value.productId === idElement.productId) {
          antragId = idElement.anfrageNummer;
        }
      });
    }
    return antragId;
  }

  private static ermittleFerienhausVorversicherung(value: any, data: any) {
    const ferienhaus = data.anfrage.ferienhaus;
    if (value.productId === ferienhaus.vgv.tarifinformationen.produktId) {
      return ferienhaus.vgv.vorversicherung;
    }
    if (value.productId === ferienhaus.vhv.tarifinformationen.produktId) {
      return ferienhaus.vhv.vorversicherung;
    }
    if (value.productId === ferienhaus.hug.tarifinformationen.produktId) {
      return ferienhaus.hug.vorversicherung;
    }
    return data.anfrage.vorversicherung;
  }

  private static ermittleBeantragenEreignis(
    status: string,
    vorversicherung: Vorversicherung,
    aussteuerungsReasons: Aussteuerungsgrund[],
  ) {
    let ereignis = "beantragt";
    if ("ABGELEHNT" === status) {
      ereignis = "beantragtAbgelehnt";
    } else if (AnfrageService.istAenderung(vorversicherung)) {
      ereignis = "beantragtAenderungsantrag";
    } else if (aussteuerungsReasons && aussteuerungsReasons.length > 0) {
      ereignis = "beantragtAusgesteuert";
    }
    return ereignis;
  }

  private static istAenderung(vorversicherung: Vorversicherung): boolean {
    if (vorversicherung.antragsart.toLowerCase() === "aenderung") {
      return true;
    }
    if (vorversicherung.antragsart.toLowerCase() === "wechsel") {
      return (
        vorversicherung.gesellschaft.toLowerCase().includes("gev") ||
        vorversicherung.gesellschaft.toLowerCase().includes("grundeigent")
      );
    }
    return false;
  }

  laden(
    sessionToken: string,
    isMakler: boolean,
    produkt: string | undefined,
    id: string,
    quelle: string | undefined,
    kontext: string,
  ) {
    Logger.debug("laden(): Vorschlag laden...", id);
    return this.http()
      .get(this.getBaseUrl(isMakler, false) + "vorschlag/" + id, {
        headers: {
          S_Token: sessionToken,
        },
      })
      .then((value) => {
        AnfrageService.track(sessionToken, kontext, "wiederaufnehmen" + (quelle ? "_" + quelle : ""), value.data);
        return value;
      });
  }

  beratungAnfordern(sessionToken: string, kontext: string, anfrage: Anfrage, ereignis: string) {
    AnfrageService.track(sessionToken, kontext, ereignis, anfrage);
  }
}

export default new AnfrageService();
